import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useController, useForm, useWatch, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { Play, Square } from 'lucide-react'

import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
  SidePanelSection,
} from '@/components/application/side-panel'
import { Button } from '@/components/base/buttons'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { FormInput } from '@/components/forms/FormInput'
import { FormTextarea } from '@/components/forms/FormTextarea'
import { FormSelect } from '@/components/forms/FormSelect'
import { FormCombobox } from '@/components/forms/FormCombobox'
import { FormSwitch } from '@/components/forms/FormSwitch'
import { FormScoreSelect } from '@/components/forms/FormScoreSelect'
import { FormSegmentedSelect } from '@/components/forms/FormSegmentedSelect'
import { ScoreBar } from '@/components/common/ScoreBar'
import { Slider } from '@/components/ui/Slider'
import { Switch } from '@/components/ui/Switch'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import { useAdminVoice, useCreateVoice, useUpdateVoice, useVoiceProviders } from '@/hooks/api'
import { useAudioPreview } from '@/hooks/useAudioPreview'
import { toast } from '@/lib/toast'
import {
  GENDER_OPTIONS,
  AGE_GROUP_OPTIONS,
  TONE_OPTIONS,
  PRICING_TIER_OPTIONS,
  LANGUAGE_DEFAULT_PROVIDER_TYPES,
  RATE_PROVIDER_TYPES,
  MODEL_SETTINGS_BY_PROVIDER_TYPE,
} from '@/lib/voiceConstants'
import { SUPPORTED_LANGUAGES, getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'
import type { Voice } from '@/types/assistant'
import type { VoiceUpdateInput, VoiceMetaInput } from '@/types/voiceAdmin'
import {
  DEFAULT_VALUES,
  formValuesToInput,
  voiceSchema,
  voiceToFormValues,
  type VoiceFormValues,
} from './voiceFormValues'

// Subtle divider above every form section after the first.
const SECTION_DIVIDER = 'border-t border-gray-200 pt-5 dark:border-gray-700'

// Backend detail prefix shared by VoiceVerificationError (400, the provider
// rejected the voice) and VoiceVerificationUnavailableError (502, the provider
// could not be reached); the remainder of the detail is the provider's reason.
const VERIFICATION_PREFIX = 'Voice verification'
const CHANGED_DURING_VERIFICATION_PREFIX = 'The voice was changed by another update'

/** The provider's reason from a verification detail, or '' when there is none. */
function verificationReason(detail: string): string {
  return detail.replace(/^Voice verification (failed|unavailable)[:\s]*/, '')
}

// --- Speech rate control -----------------------------------------------------

const RATE_MIN = 0.5
const RATE_MAX = 2
const RATE_STEP = 0.05
/** String written into the form when the admin switches off "provider default". */
const RATE_INITIAL = '1.0'
/** Horizontal position of the 1.0 marker within the 0.5–2.0 track. */
const RATE_STANDARD_PERCENT = ((1 - RATE_MIN) / (RATE_MAX - RATE_MIN)) * 100

/**
 * Format a slider value as the technical string stored in the form: always
 * dot-decimal, at least one decimal ('1.0', '0.85', '1.25').
 */
function formatRate(value: number): string {
  const fixed = value.toFixed(2)
  return fixed.endsWith('0') ? fixed.slice(0, -1) : fixed
}

/**
 * Slider + linked number input for `rate`, with a "provider default" toggle.
 * The toggle is its own form field (`rate_default`), so clearing the number
 * input to retype a value never flips the toggle or disables the input; the
 * schema requires a rate whenever the toggle is off.
 */
function RateField({ control }: { control: Control<VoiceFormValues> }) {
  const { t } = useTranslation()
  const {
    field,
    fieldState: { error },
  } = useController({ control, name: 'rate' })
  const { field: defaultField } = useController({ control, name: 'rate_default' })

  const isProviderDefault = defaultField.value
  const parsed = Number(field.value)
  const sliderValue =
    !isProviderDefault && field.value.trim() !== '' && Number.isFinite(parsed)
      ? Math.min(RATE_MAX, Math.max(RATE_MIN, parsed))
      : 1

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
        {t('resources.admin.voices.fields.rate')}
      </span>
      <Switch
        size="sm"
        label={t('resources.admin.voices.fields.rate_default')}
        isSelected={isProviderDefault}
        onChange={(selected) => {
          defaultField.onChange(selected)
          if (!selected && field.value.trim() === '') field.onChange(RATE_INITIAL)
        }}
      />
      <div className={cn('flex items-center gap-3', isProviderDefault && 'opacity-50')}>
        <div className="min-w-0 flex-1 pb-5">
          <div className="relative">
            <Slider
              aria-label={t('resources.admin.voices.fields.rate')}
              minValue={RATE_MIN}
              maxValue={RATE_MAX}
              step={RATE_STEP}
              value={sliderValue}
              onChange={(value) =>
                field.onChange(formatRate(Array.isArray(value) ? value[0] : value))
              }
              isDisabled={isProviderDefault}
            />
            <div
              className="pointer-events-none absolute top-full flex -translate-x-1/2 flex-col items-center"
              style={{ left: `${RATE_STANDARD_PERCENT}%` }}
            >
              <div className="h-1.5 w-px bg-gray-300 dark:bg-gray-600" />
              <span className="text-[11px] whitespace-nowrap text-gray-400 dark:text-gray-500">
                1.0 · {t('resources.admin.voices.rate_standard')}
              </span>
            </div>
          </div>
        </div>
        <Input
          type="number"
          aria-label={t('resources.admin.voices.fields.rate')}
          value={field.value}
          onChange={(value) => field.onChange(value)}
          isDisabled={isProviderDefault}
          className="w-24 shrink-0"
        />
      </div>
      {error?.message && (
        <p className="text-error-600 dark:text-error-400 text-sm">{t(error.message)}</p>
      )}
    </div>
  )
}

interface VoiceFormDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** Voice to edit; omit/null for create mode. */
  voice?: Voice | null
}

export function VoiceFormDrawer({ isOpen, onClose, voice }: VoiceFormDrawerProps) {
  const { t, i18n } = useTranslation()
  const isEdit = !!voice
  const isInUse = !!voice?.in_use

  const createVoice = useCreateVoice()
  const updateVoice = useUpdateVoice()
  // Edit against a fresh read, not the list row: meta/config are replaced whole
  // on save, so a stale row would revert another admin's edit.
  const { data: freshVoice } = useAdminVoice(voice?.id, isOpen && isEdit)
  const editTarget = freshVoice ?? voice
  const {
    data: providers = [],
    isLoading: isProvidersLoading,
    isError: isProvidersError,
    refetch: refetchProviders,
  } = useVoiceProviders(isOpen)

  const { control, handleSubmit, reset, setValue, formState } = useForm<VoiceFormValues>({
    resolver: zodResolver(voiceSchema),
    defaultValues: DEFAULT_VALUES,
  })
  const { isDirty, isSubmitting, dirtyFields } = formState

  // Reset the form whenever the drawer opens or the target voice changes.
  useEffect(() => {
    if (isOpen) {
      reset(voice ? voiceToFormValues(voice) : DEFAULT_VALUES)
    }
  }, [isOpen, voice, reset])

  // Re-seed from the fresh read once it lands, unless the admin already started
  // typing against the cached row.
  const seededFrom = useRef<Voice | null>(null)
  useEffect(() => {
    if (!isOpen || !freshVoice || seededFrom.current === freshVoice) return
    seededFrom.current = freshVoice
    if (!isDirty) reset(voiceToFormValues(freshVoice))
  }, [isOpen, freshVoice, isDirty, reset])

  const watchedLanguages = useWatch({ control, name: 'languages' })
  const selectedLanguages = useMemo(() => watchedLanguages ?? [], [watchedLanguages])
  const watchedNativeLanguages = useWatch({ control, name: 'native_languages' })
  const selectedNativeLanguages = useMemo(
    () => watchedNativeLanguages ?? [],
    [watchedNativeLanguages]
  )
  const watchedDefaultFor = useWatch({ control, name: 'default_for' })
  const selectedDefaultFor = useMemo(() => watchedDefaultFor ?? [], [watchedDefaultFor])
  const voiceIdValue = useWatch({ control, name: 'voice_id' })
  const suggested = useWatch({ control, name: 'suggested' })

  // Live preview of the customer-facing ScoreBars ("how customers see it").
  const naturalnessValue = useWatch({ control, name: 'naturalness' })
  const speedValue = useWatch({ control, name: 'speed' })
  const naturalnessScore = naturalnessValue?.trim() ? Number(naturalnessValue) : null
  const speedScore = speedValue?.trim() ? Number(speedValue) : null
  const hasScorePreview = naturalnessScore != null || speedScore != null

  // Language options with the name localized to the UI language (flag emoji
  // kept in front), falling back to the canonical English name when
  // Intl.DisplayNames has no answer for the code.
  const languageOptions = useMemo(
    () =>
      SUPPORTED_LANGUAGES.map((lang) => ({
        value: lang.id,
        label: `${getLanguageFlag(lang.id)} ${getLanguageDisplayName(lang.id, i18n.language) ?? lang.name}`,
      })),
    [i18n.language]
  )

  // native_languages and default_for must be within the selected languages.
  const selectedLanguageOptions = useMemo(
    () => languageOptions.filter((opt) => selectedLanguages.includes(opt.value)),
    [languageOptions, selectedLanguages]
  )

  // Drop native languages that were deselected.
  useEffect(() => {
    const kept = selectedNativeLanguages.filter((lang) => selectedLanguages.includes(lang))
    if (kept.length !== selectedNativeLanguages.length) {
      setValue('native_languages', kept)
    }
  }, [selectedNativeLanguages, selectedLanguages, setValue])

  // Drop language defaults for languages that were deselected.
  useEffect(() => {
    const kept = selectedDefaultFor.filter((lang) => selectedLanguages.includes(lang))
    if (kept.length !== selectedDefaultFor.length) {
      setValue('default_for', kept)
    }
  }, [selectedDefaultFor, selectedLanguages, setValue])

  // Prefill the languages field with the provider's defaults while the admin
  // has not selected any languages yet. Azure has no static default: the
  // locale prefix of the provider voice id decides (mono-locale voices speak
  // only their own locale; Multilingual voices get the full app-locale list).
  // Applied via an explicit button so the fields never change behind the
  // admin's back (and existing voices can be reset to defaults too). Besides
  // languages the button also fills the provider's speed-score baseline;
  // naturalness stays a per-voice editorial call.
  const providerIdValue = useWatch({ control, name: 'provider_id' })
  const providerDefaults = useMemo(() => {
    const provider = providers.find((p) => String(p.id) === providerIdValue)
    if (!provider) return null
    const speed = provider.default_speed_score ?? null
    let languages: string[] = []
    if (provider.provider_type === 'azure') {
      if ((voiceIdValue ?? '').includes('Multilingual')) {
        languages = SUPPORTED_LANGUAGES.map((lang) => lang.id)
      } else {
        // A mono-locale voice needs no native list: one language, native by definition.
        const locale = /^([a-z]{2}-[A-Z]{2})-/.exec(voiceIdValue ?? '')?.[1]
        if (locale) languages = [locale]
      }
    } else {
      languages = provider.default_languages ?? []
    }
    if (languages.length === 0 && speed == null) return null
    return { languages, speed }
  }, [providers, providerIdValue, voiceIdValue])

  // Only pipeline providers can hold a language default (backend guard); the
  // field is disabled and emptied for the others.
  const canHoldLanguageDefault = useMemo(() => {
    const provider = providers.find((p) => String(p.id) === providerIdValue)
    return !provider || LANGUAGE_DEFAULT_PROVIDER_TYPES.includes(provider.provider_type)
  }, [providers, providerIdValue])

  // Only Azure and ElevenLabs read `config.rate`; for other providers the
  // control is hidden (any stored value is preserved untouched).
  const showRate = useMemo(() => {
    const provider = providers.find((p) => String(p.id) === providerIdValue)
    return !provider || RATE_PROVIDER_TYPES.includes(provider.provider_type)
  }, [providers, providerIdValue])

  // Only ElevenLabs and Mistral voices pick a model here; hidden for every other
  // provider (any stored value is preserved untouched).
  const modelSettings = useMemo(() => {
    const provider = providers.find((p) => String(p.id) === providerIdValue)
    return provider ? MODEL_SETTINGS_BY_PROVIDER_TYPE[provider.provider_type] : undefined
  }, [providers, providerIdValue])

  // A stored model outside the known list stays selectable, so opening and
  // saving the form never silently swaps it.
  const modelIdValue = useWatch({ control, name: 'model_id' })
  const modelOptions = useMemo(() => {
    const options = [
      { value: '', label: t('resources.admin.voices.fields.model_default') },
      ...(modelSettings?.options ?? []),
    ]
    if (modelIdValue && !options.some((opt) => opt.value === modelIdValue)) {
      options.push({ value: modelIdValue, label: modelIdValue })
    }
    return options
  }, [modelSettings, modelIdValue, t])

  useEffect(() => {
    if (!canHoldLanguageDefault && selectedDefaultFor.length > 0) {
      setValue('default_for', [])
    }
  }, [canHoldLanguageDefault, selectedDefaultFor, setValue])

  const applyProviderDefaults = () => {
    if (!providerDefaults) return
    const { languages, speed } = providerDefaults
    if (languages.length > 0) {
      setValue('languages', languages, { shouldDirty: true })
    }
    if (speed != null) {
      setValue('speed', String(speed), { shouldDirty: true })
    }
    // Both affected fields live below the button — announce what was filled.
    if (languages.length > 0 && speed != null) {
      toast.success(
        t('resources.admin.voices.helpers.provider_defaults_applied', {
          count: languages.length,
          speed,
        })
      )
    } else if (speed != null) {
      toast.success(
        t('resources.admin.voices.helpers.provider_defaults_applied_speed_only', { speed })
      )
    } else if (languages.length > 0) {
      toast.success(
        t('resources.admin.voices.helpers.provider_defaults_applied_languages_only', {
          count: languages.length,
        })
      )
    }
  }

  const providerOptions = useMemo(
    () =>
      providers.map((p) => ({
        value: String(p.id),
        label: p.provider_type ? `${p.name} (${p.provider_type})` : p.name,
      })),
    [providers]
  )

  // --- Audio preview (shared play/stop + cleanup state machine) ---
  const {
    playingId,
    loadingId,
    toggle: togglePreview,
    stop: stopPreview,
  } = useAudioPreview({
    errorMessage: t('resources.admin.voices.errors.preview_failed'),
  })
  const isPreviewLoading = !!voice && loadingId === voice.id
  const isPlaying = !!voice && playingId === voice.id && !isPreviewLoading

  // Stop playback when the open "session" ends: the drawer closing or the
  // component unmounting while open. The SidePanel only unmounts its inner
  // content on close - this component (which owns the audio preview) stays
  // mounted, so the hook's unmount-only cleanup would let audio play on after
  // close. Audio can only start while the drawer is open, so closed-state
  // unmounts have nothing to clean up.
  useEffect(() => {
    if (!isOpen) return undefined
    return stopPreview
  }, [isOpen, stopPreview])

  // Preview is only meaningful for an existing voice (needs a persisted id), and
  // it plays the *saved* voice: unsaved identity or rate edits are not audible.
  const canPreview = isEdit && !!voice && !!voiceIdValue
  const previewOutdated = !!(
    dirtyFields.voice_id ||
    dirtyFields.provider_id ||
    dirtyFields.rate ||
    dirtyFields.rate_default ||
    dirtyFields.model_id
  )

  const handlePreview = useCallback(() => {
    if (!voice) return
    // Same rule as the backend probe: first native language, else first supported.
    const language = selectedNativeLanguages[0] || selectedLanguages[0] || 'en-US'
    // No `text`: the backend picks a localized sample greeting for the language.
    void togglePreview(voice.id, {
      language,
      id: 0,
      voice_id: voice.id,
    })
  }, [voice, togglePreview, selectedNativeLanguages, selectedLanguages])

  const localizedOptions = (options: { value: string; labelKey: string }[]) =>
    options.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))

  const onSubmit = useCallback(
    async (values: VoiceFormValues) => {
      const input = formValuesToInput(
        values,
        editTarget?.config ?? {},
        (editTarget?.meta ?? {}) as VoiceMetaInput
      )
      try {
        if (isEdit && voice) {
          const update: VoiceUpdateInput = { id: voice.id, ...input }
          // Never send identity fields for an in-use voice (server rejects them).
          if (isInUse) {
            delete update.voice_id
            delete update.provider_id
          }
          await updateVoice.mutateAsync(update)
          toast.success(t('resources.admin.voices.updated'))
        } else {
          await createVoice.mutateAsync(input)
          toast.success(t('resources.admin.voices.created'))
        }
        onClose()
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status
          const detail: unknown = error.response?.data?.detail
          if (status === 409) {
            // A concurrent write during the synthesis probe is not a conflict
            // with the admin's input: the right action is simply to save again.
            const changedDuringVerification =
              typeof detail === 'string' && detail.startsWith(CHANGED_DURING_VERIFICATION_PREFIX)
            toast.error(
              t(
                changedDuringVerification
                  ? 'resources.admin.voices.errors.changed_during_verification'
                  : 'resources.admin.voices.errors.conflict'
              )
            )
            return
          }
          if (status === 502) {
            // The provider could not be reached: nothing is wrong with the input.
            const reason = typeof detail === 'string' ? verificationReason(detail) : ''
            const base = t('resources.admin.voices.errors.verification_unavailable')
            toast.error(reason ? `${base} — ${reason}` : base)
            return
          }
          if (status === 400 || status === 422) {
            // Provider verification failures carry the provider's reason in the
            // detail string; surface it so the admin can fix the voice ID.
            if (typeof detail === 'string' && detail.startsWith(VERIFICATION_PREFIX)) {
              const reason = verificationReason(detail)
              const base = t('resources.admin.voices.errors.verification_failed')
              toast.error(reason ? `${base} — ${reason}` : base)
              return
            }
            // Other 400 details (meta/config validation, e.g. default_for rules)
            // are admin-readable messages from the service; a 422 detail is
            // Pydantic's array and gets the generic message.
            toast.error(
              typeof detail === 'string' ? detail : t('resources.admin.voices.errors.invalid_meta')
            )
            return
          }
        }
        toast.error(
          isEdit
            ? t('resources.admin.voices.errors.update_failed')
            : t('resources.admin.voices.errors.create_failed')
        )
      }
    },
    [isEdit, voice, editTarget, isInUse, updateVoice, createVoice, onClose, t]
  )

  // The form fields are fully controlled (no refs registered with RHF), so
  // shouldFocusError/setFocus cannot scroll an off-screen invalid field into
  // view; the toast is the invalid-submit signal instead.
  const onInvalid = useCallback(() => {
    toast.error(t('resources.admin.voices.errors.validation'))
  }, [t])

  // --- Dirty guard: intercept every close route (Cancel, header X, Esc and
  // overlay dismiss) and confirm before discarding unsaved changes. ---
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)

  const requestClose = useCallback(() => {
    if (isSubmitting) return
    if (isDirty) {
      setShowUnsavedDialog(true)
      return
    }
    onClose()
  }, [isSubmitting, isDirty, onClose])

  const handleDiscard = useCallback(() => {
    setShowUnsavedDialog(false)
    onClose()
  }, [onClose])

  const identityLockedHelp = isInUse ? t('resources.admin.voices.locked_in_use') : undefined

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onOpenChange={(open) => !open && requestClose()}
        side="right"
        size="lg"
        variant="form"
      >
        <SidePanelContent>
          <SidePanelHeader
            title={
              isEdit
                ? t('resources.admin.voices.edit_title')
                : t('resources.admin.voices.create_title')
            }
            subtitle={t('resources.admin.voices.form_subtitle')}
            onClose={requestClose}
          />

          <SidePanelBody>
            <form
              id="voice-form"
              onSubmit={handleSubmit(onSubmit, onInvalid)}
              className="flex flex-col gap-6"
            >
              {/* Hidden native submit control: keeps Enter-key implicit
                  submission working for this multi-input form — the visible
                  footer button sits outside the form and invokes the RHF
                  handler directly. */}
              <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
              {/* Identity */}
              <SidePanelSection
                title={t('resources.admin.voices.sections.identity')}
                description={t('resources.admin.voices.sections.identity_description')}
              >
                <FormInput
                  control={control}
                  name="display_name"
                  label={t('resources.admin.voices.fields.display_name')}
                  isRequired
                />
                <FormInput
                  control={control}
                  name="voice_id"
                  label={t('resources.admin.voices.fields.voice_id')}
                  isRequired
                  isDisabled={isInUse}
                  description={identityLockedHelp}
                />
                <FormSelect
                  control={control}
                  name="provider_id"
                  label={t('resources.admin.voices.fields.provider')}
                  isRequired
                  isDisabled={isInUse || isProvidersLoading}
                  options={providerOptions}
                  description={
                    isProvidersLoading ? t('resources.generic.loading') : identityLockedHelp
                  }
                />
                {/* Provider-level action: fills languages and the speed score
                    below. A toast announces exactly what was filled, since
                    both fields live further down the form. */}
                <div>
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onPress={applyProviderDefaults}
                    isDisabled={!providerDefaults}
                  >
                    {t('resources.admin.voices.fields.apply_default_languages')}
                  </Button>
                  {!providerDefaults && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('resources.admin.voices.helpers.apply_default_languages_hint')}
                    </p>
                  )}
                </div>
                {isProvidersError && (
                  <div className="text-error-600 dark:text-error-400 flex items-center gap-2 text-sm">
                    <span>{t('resources.admin.voices.errors.providers_failed')}</span>
                    <Button
                      type="button"
                      color="link-color"
                      size="sm"
                      onPress={() => refetchProviders()}
                    >
                      {t('components.voice_call.retry')}
                    </Button>
                  </div>
                )}
                <FormTextarea
                  control={control}
                  name="description"
                  label={t('resources.admin.voices.fields.description')}
                  isRequired
                  rows={2}
                />
                {canPreview && (
                  <div className="flex flex-col gap-1 self-start">
                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      iconLeading={isPlaying ? Square : Play}
                      onPress={handlePreview}
                      isLoading={isPreviewLoading}
                      isDisabled={previewOutdated && !isPlaying}
                      showTextWhileLoading
                    >
                      {isPlaying
                        ? t('resources.admin.voices.stop_preview')
                        : t('resources.admin.voices.preview_saved')}
                    </Button>
                    {previewOutdated && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('resources.admin.voices.helpers.preview_saved_voice')}
                      </p>
                    )}
                  </div>
                )}
              </SidePanelSection>

              {/* Languages */}
              <SidePanelSection
                title={t('resources.admin.voices.sections.languages')}
                description={t('resources.admin.voices.sections.languages_description')}
                className={SECTION_DIVIDER}
              >
                <FormCombobox
                  control={control}
                  name="languages"
                  multiple
                  label={t('resources.admin.voices.fields.languages')}
                  options={languageOptions}
                  isRequired
                />
                <FormCombobox
                  control={control}
                  name="native_languages"
                  multiple
                  label={t('resources.admin.voices.fields.native_languages')}
                  description={t('resources.admin.voices.helpers.native_languages')}
                  options={selectedLanguageOptions}
                />
                <FormCombobox
                  control={control}
                  name="default_for"
                  multiple
                  label={t('resources.admin.voices.fields.default_for')}
                  description={
                    canHoldLanguageDefault
                      ? t('resources.admin.voices.helpers.default_for')
                      : t('resources.admin.voices.helpers.default_for_unavailable')
                  }
                  options={selectedLanguageOptions}
                  isDisabled={!canHoldLanguageDefault}
                />
              </SidePanelSection>

              {/* Characteristics */}
              <SidePanelSection
                title={t('resources.admin.voices.sections.characteristics')}
                description={t('resources.admin.voices.sections.characteristics_description')}
                className={SECTION_DIVIDER}
              >
                <FormSelect
                  control={control}
                  name="gender"
                  label={t('resources.admin.voices.fields.gender')}
                  options={localizedOptions(GENDER_OPTIONS)}
                />
                <FormSelect
                  control={control}
                  name="age_group"
                  label={t('resources.admin.voices.fields.age_group')}
                  options={localizedOptions(AGE_GROUP_OPTIONS)}
                />
                <FormCombobox
                  control={control}
                  name="tones"
                  multiple
                  label={t('resources.admin.voices.fields.tones')}
                  options={localizedOptions(TONE_OPTIONS)}
                />
              </SidePanelSection>

              {/* Scores */}
              <SidePanelSection
                title={t('resources.admin.voices.sections.scores')}
                description={t('resources.admin.voices.sections.scores_description')}
                className={SECTION_DIVIDER}
              >
                <FormScoreSelect
                  control={control}
                  name="naturalness"
                  label={t('resources.admin.voices.fields.naturalness')}
                  clearLabel={t('resources.admin.voices.scores.clear')}
                  segmentLabel={(value, max) =>
                    t('resources.admin.voices.aria.score_segment', { value, max })
                  }
                />
                <FormScoreSelect
                  control={control}
                  name="speed"
                  label={t('resources.admin.voices.fields.speed')}
                  clearLabel={t('resources.admin.voices.scores.clear')}
                  segmentLabel={(value, max) =>
                    t('resources.admin.voices.aria.score_segment', { value, max })
                  }
                />
                {hasScorePreview && (
                  <div className="flex flex-col gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('resources.admin.voices.scores.preview')}
                    </span>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {t('resources.admin.voices.fields.naturalness')}
                        </span>
                        <ScoreBar
                          score={naturalnessScore}
                          label={t('resources.admin.voices.fields.naturalness')}
                          notScoredLabel={t('resources.admin.voices.not_scored')}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {t('resources.admin.voices.fields.speed')}
                        </span>
                        <ScoreBar
                          score={speedScore}
                          label={t('resources.admin.voices.fields.speed')}
                          notScoredLabel={t('resources.admin.voices.not_scored')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </SidePanelSection>

              {(showRate || !!modelSettings) && (
                <SidePanelSection
                  title={t('resources.admin.voices.sections.provider_settings')}
                  description={t('resources.admin.voices.sections.provider_settings_description')}
                  className={SECTION_DIVIDER}
                >
                  {modelSettings && (
                    <FormSelect
                      control={control}
                      name="model_id"
                      label={t('resources.admin.voices.fields.model')}
                      options={modelOptions}
                      description={t(modelSettings.helperKey)}
                    />
                  )}
                  {showRate && <RateField control={control} />}
                </SidePanelSection>
              )}

              {/* Recommendation */}
              <SidePanelSection
                title={t('resources.admin.voices.sections.recommendation')}
                description={t('resources.admin.voices.sections.recommendation_description')}
                className={SECTION_DIVIDER}
              >
                <FormSwitch
                  control={control}
                  name="suggested"
                  label={t('resources.admin.voices.fields.recommended')}
                  description={t('resources.admin.voices.helpers.recommended')}
                />
                <FormInput
                  control={control}
                  name="suggested_weight"
                  type="number"
                  label={t('resources.admin.voices.fields.priority')}
                  description={t('resources.admin.voices.helpers.priority')}
                  isDisabled={!suggested}
                />
              </SidePanelSection>

              {/* Pricing & visibility */}
              <SidePanelSection
                title={t('resources.admin.voices.sections.visibility')}
                description={t('resources.admin.voices.sections.visibility_description')}
                className={SECTION_DIVIDER}
              >
                <FormSegmentedSelect
                  control={control}
                  name="pricing_tier"
                  label={t('resources.admin.voices.fields.pricing_tier')}
                  options={localizedOptions(PRICING_TIER_OPTIONS)}
                />
                <FormSwitch
                  control={control}
                  name="is_public"
                  label={t('resources.admin.voices.fields.is_public')}
                  description={t('resources.admin.voices.helpers.is_public')}
                />
              </SidePanelSection>
            </form>
          </SidePanelBody>

          <SidePanelFooter>
            <Button
              type="button"
              color="secondary"
              onPress={requestClose}
              isDisabled={isSubmitting}
            >
              {t('components.confirm_dialog.cancel')}
            </Button>
            {/* react-aria Button suppresses the native click default, so the
                form="voice-form" association never fires a submit — invoke the
                RHF submit handler directly instead. */}
            <Button
              type="button"
              color="primary"
              isLoading={isSubmitting}
              onPress={() => void handleSubmit(onSubmit, onInvalid)()}
            >
              {isEdit ? t('resources.admin.voices.save') : t('resources.admin.voices.create')}
            </Button>
          </SidePanelFooter>
        </SidePanelContent>
      </SidePanel>

      <AlertDialog
        isOpen={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        onConfirm={handleDiscard}
        variant="warning"
        title={t('resources.admin.voices.unsaved.title')}
        description={t('resources.admin.voices.unsaved.description')}
        confirmLabel={t('resources.admin.voices.unsaved.discard')}
        cancelLabel={t('resources.admin.voices.unsaved.keep')}
        loadingLabel={t('resources.generic.loading')}
      />
    </>
  )
}

export default VoiceFormDrawer
